import React, { useMemo, useState } from 'react';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useReward } from 'react-rewards';
import { Virtuoso } from 'react-virtuoso';
import { ActivityFeedGroupInfoTypenames, enumParser } from '@soundxyz/utils/const';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { theme } from '@components-shared/src/stitches';
import { FeedActivityGroup } from '@components/Feed/FeedActivityGroup';
import { FeedHeader } from '@components/Feed/FeedHeader';
import { FeedPostFloatingButton } from '@components/Feed/FeedPostFloatingButton';
import { NewActivityButton } from '@components/Feed/NewActivityButton';
import { FeedEmpty, FeedEmptyAuth, FeedError } from '@components/Feed/Placeholders';
import type { ActivityGroupWithDivider } from '@components/Feed/store';
import { useActivityFeedState } from '@components/Feed/store';
import { useActivityFeed } from '@components/Feed/useActivityFeed';
import { useActivityFeedSubscription } from '@components/Feed/useActivityFeedSubscription';
import { Seo } from '@components/Seo';
import { TopPosts } from '@components/TopPosts/indext';
import { TrendingRecommendedFollows } from '@components/TrendingRecommendedFollows';
import { TwoColumnPage } from '@components/TwoColumnPage';
import {
  AUDIO_PLAYING_MOBILE_POPUP_BUTTON_OFFSET,
  AUDIO_PLAYING_TABLET_POPUP_BUTTON_OFFSET,
  MOBILE_POPUP_BUTTON_OFFSET,
  TABLET_POPUP_BUTTON_OFFSET,
} from '@components/basics/PopupButton';
import { Spinner } from '@components/basics/Spinner';
import { Divider } from '@components/main-components/divider';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { MediaQuery } from '@context/mediaQuery';
import { ToastContext } from '@context/toast';
import { ActivityFeedGroupFilterOption } from '@graphql/reactQuery';
import { useBackendUserPersistedData } from '@hooks/useBackendUserPersistedData';
import { useIsSsr } from '@hooks/useIsSsr';
import { useLogError } from '@hooks/useLogError';
import { usePopState } from '@hooks/usePopState';
import { useAudioController } from '@store/audio/AudioController';
import { wait } from '@utils/helpers';

export const parseActivityFeedGroupInfoTypenames = enumParser(ActivityFeedGroupInfoTypenames);

const mapAggregateToFilterType = (
  aggregate: ActivityFeedGroupInfoTypenames,
): ActivityFeedGroupFilterOption => {
  switch (aggregate) {
    case 'ReleaseDroppedAggregate':
      return ActivityFeedGroupFilterOption.ReleaseDropped;
    case 'SongCollectedByManyAggregate':
      return ActivityFeedGroupFilterOption.Collected;
    case 'UserCollectedManySongsAggregate':
      return ActivityFeedGroupFilterOption.Collected;
    case 'ShelfCreatedAggregate':
      return ActivityFeedGroupFilterOption.AddedToPlaylist;
    case 'ReleasesAddedToShelfAggregate':
      return ActivityFeedGroupFilterOption.AddedToPlaylist;
    case 'UserLikedPlaylistAggregate':
      return ActivityFeedGroupFilterOption.Likes;
    case 'UserLikedSongsAggregate':
      return ActivityFeedGroupFilterOption.Likes;
    case 'UserCreatedPostAggregate':
      return ActivityFeedGroupFilterOption.Posts;
    default:
      return ActivityFeedGroupFilterOption.Collected;
  }
};

export const getStaticProps: GetStaticProps<Record<string, never>> = async () => {
  return {
    props: {},
    // rebuild every 1 minute
    revalidate: 60,
  };
};

export type PageProps = InferGetStaticPropsType<typeof getStaticProps>;

const renderItemContent = (index: number, item: ActivityGroupWithDivider) => (
  <FeedActivityGroup index={index} key={`feed-activity-group-${index}`} item={item} />
);

export default React.memo(function ActivityFeed() {
  const isSsr = useIsSsr();
  const [newActivityCount, setNewActivityCount] = useState(0);
  const [isFetchingNewActivity, setIsFetchingNewActivity] = useState(false);

  const {
    data,
    loadMore,
    loading,
    id,
    setActivityFeedGroupFilter,
    activityFeedGroupFilter,
    setActivityFeedFilter,
    activityFeedFilter,
    refetch,
    error,
    fetchedFilterSettingsFromLocalStorage,
    hasNextPage,
  } = useActivityFeed({ disabled: false });

  const { tablet } = MediaQuery.useContainer();
  const { activeTrackId, hideAudioPlayer } = useAudioController();
  const { openToast } = ToastContext.useContainer();
  const { authUser } = useAuth();
  const { isConnectingWallet } = useConnectedAuth();
  const { scrollIndex: globalScrollIndex } = useActivityFeedState();
  const { isPopStateEvent } = usePopState();

  const showEmptyState =
    data.length === 0 &&
    fetchedFilterSettingsFromLocalStorage &&
    !isFetchingNewActivity &&
    !loading &&
    !error &&
    !isConnectingWallet;

  const { reward } = useReward('rewardId', 'emoji', {
    elementCount: 5,
    emoji: ['🚀', '🎵'],
    zIndex: parseInt(theme.zIndices.above3.value, 10),
    lifetime: 500,
  });

  /**
   * Scroll to the top while fetching new activity
   * Browser scroll animation can be interrupted when elements in the dom are replaced
   * To avoid leaving the user in a half-way-scroll-to-top state, we add fallbacks to scroll to the top without animation
   */
  const handleFetchNewActivity = useStableCallback(async () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    await wait(200);
    setIsFetchingNewActivity(true);
    await wait(50);
    reward();
    window.scrollTo({ top: 0 });
    await refetch();
    window.scrollTo({ top: 0 });
    setNewActivityCount(0);
    setIsFetchingNewActivity(false);
  });

  const logError = useLogError();

  useActivityFeedSubscription({
    activityFeedId: id,
    onSubscriptionData({
      data: {
        activityFeedNewGroup: { activityFeedGroupInformationTypename: type },
      },
    }) {
      try {
        const satisfiesFilters =
          activityFeedGroupFilter.includes(ActivityFeedGroupFilterOption.All) ||
          (activityFeedGroupFilter.length
            ? activityFeedGroupFilter.includes(
                mapAggregateToFilterType(parseActivityFeedGroupInfoTypenames(type)),
              )
            : true);

        if (satisfiesFilters) {
          setNewActivityCount(prev => prev + 1);
        }
      } catch (error) {
        logError({ error });
      }
    },
  });

  const hasError = !!error;
  const dataSize = data.length;

  React.useEffect(() => {
    if (hasError && dataSize !== 0) {
      openToast({
        text: 'An error occurred with the activity feed.',
        variant: 'error',
      });
    }
  }, [hasError, dataSize, openToast]);

  // Hide and reset new activity button when filters are updated
  React.useEffect(() => {
    setNewActivityCount(0);
  }, [activityFeedFilter, activityFeedGroupFilter]);

  // Needed for SSR side effects virtuoso

  const mobileOffset =
    activeTrackId && !hideAudioPlayer
      ? AUDIO_PLAYING_MOBILE_POPUP_BUTTON_OFFSET
      : MOBILE_POPUP_BUTTON_OFFSET;

  const tabletOffset =
    activeTrackId && !hideAudioPlayer
      ? AUDIO_PLAYING_TABLET_POPUP_BUTTON_OFFSET
      : TABLET_POPUP_BUTTON_OFFSET;

  const newActivityBottomOffset = tablet ? tabletOffset : mobileOffset;

  const memoizedFooter = useMemo(
    () => (hasNextPage || loading ? <Spinner className="py-5 text-neutral400" /> : null),
    [hasNextPage, loading],
  );

  const { hasFeedAccess } = useBackendUserPersistedData();

  const hasPostingAccess = hasFeedAccess.type === 'has-access';

  return (
    <>
      <Seo
        title="Activity Feed | Sound.xyz"
        image="https://d16zqvkniqf1rn.cloudfront.net/feed_og_image.png"
      />
      <TwoColumnPage
        leftContent={
          <div className="flex w-full flex-1 flex-col">
            <FeedHeader
              setActivityFeedGroupFilter={setActivityFeedGroupFilter}
              activityFeedGroupFilter={activityFeedGroupFilter}
              setActivityFeedFilter={setActivityFeedFilter}
              activityFeedFilter={activityFeedFilter}
              loading={!fetchedFilterSettingsFromLocalStorage}
            />

            <div className="pb-5">
              <Divider />
            </div>

            {!!error && <FeedError mainFeed divider />}
            {showEmptyState && <>{!!authUser ? <FeedEmpty /> : <FeedEmptyAuth />}</>}

            {data?.length > 0 ? (
              <Virtuoso
                useWindowScroll
                data={data || []}
                itemContent={renderItemContent}
                endReached={loadMore}
                components={{ Footer: () => memoizedFooter }}
                style={{ willChange: 'height' }}
                increaseViewportBy={{ bottom: 4000, top: 500 }}
                // Scroll to global state scroll index if popstate event (user used back or forward button in browser)
                // Used to avoid scroll jumping when user switches between tabs
                // Virtuoso breaks if we pass undefined, so we pass an empty object instead
                {...(isPopStateEvent && globalScrollIndex
                  ? {
                      initialTopMostItemIndex: globalScrollIndex,
                    }
                  : {})}
              />
            ) : (
              memoizedFooter
            )}

            {!isSsr ? (
              <NewActivityButton
                newActivityCount={newActivityCount}
                onClick={handleFetchNewActivity}
                bottom={newActivityBottomOffset}
                loading={isFetchingNewActivity}
              />
            ) : null}
          </div>
        }
        rightContent={
          <div className="flex flex-col gap-10">
            <TopPosts />
            <TrendingRecommendedFollows />
          </div>
        }
      />
      {hasPostingAccess && <FeedPostFloatingButton />}
    </>
  );
});
